import { useEffect, useState } from "react";
import { CommitteeDraft, CommitteeForm } from "@/components/governance/CommitteeForm";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Committee, CommitteeMember } from "@/types/governance";

const defaultDraft: CommitteeDraft = {
  name: "",
  description: "",
  ownerId: null,
  type: "standing",
  memberIds: [],
};

interface CommitteeSheetFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentCommittee?: Committee;
  committeeToEdit?: Committee;
  members: CommitteeMember[];
  onSave: (draft: CommitteeDraft) => void;
}

export function CommitteeSheetForm({
  open,
  onOpenChange,
  parentCommittee,
  committeeToEdit,
  members,
  onSave,
}: CommitteeSheetFormProps) {
  const [draft, setDraft] = useState<CommitteeDraft>(defaultDraft);

  useEffect(() => {
    if (!open) return;
    if (committeeToEdit) {
      setDraft({
        name: committeeToEdit.name,
        description: committeeToEdit.description,
        ownerId: committeeToEdit.ownerId,
        type: committeeToEdit.type,
        memberIds: committeeToEdit.memberIds,
      });
      return;
    }
    setDraft(defaultDraft);
  }, [open, parentCommittee?.id, committeeToEdit]);

  const level = committeeToEdit ? committeeToEdit.level : (parentCommittee?.level ?? 0) + 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {committeeToEdit
              ? `Edit ${committeeToEdit.name}`
              : parentCommittee
                ? "Add Sub-Committee"
                : "Add Level 1 Committee"}
          </DialogTitle>
          <DialogDescription>
            {committeeToEdit
              ? "Update committee details and save your changes."
              : parentCommittee
              ? `Adding committee under: ${parentCommittee.name}`
              : "Start your hierarchy by adding a top-level committee."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <CommitteeForm
            value={draft}
            members={members}
            onChange={setDraft}
            parentName={parentCommittee?.name}
            level={level}
          />
        </div>

        <DialogFooter className="mt-8">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!draft.name.trim()}
            onClick={() => {
              onSave(draft);
              onOpenChange(false);
            }}
          >
            {committeeToEdit ? "Save Changes" : "Save Committee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
